import React, { useEffect, useState, useContext } from 'react';
import api from '../services/api';
import { Link } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';

const CourseManagementPage = () => {
  const [courses, setCourses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTeacher, setSelectedTeacher] = useState('');
  const [assigningCourse, setAssigningCourse] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // all, assigned, unassigned
  const { user } = useContext(AuthContext);
  const isAdmin = user?.role === 'admin';

  const fetchCourses = () => {
    setLoading(true);
    api.get('/courses', { params: { limit: 1000 } })
      .then(res => setCourses(res.data.data || res.data))
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  };

  const fetchTeachers = () => {
    api.get('/users', { params: { role: 'teacher' } })
      .then(res => {
        // Filter to ensure only teachers are included
        const teachersOnly = res.data.filter(user => user.role === 'teacher');
        setTeachers(teachersOnly);
      })
      .catch(err => console.error('Error fetching teachers:', err));
  };

  useEffect(() => {
    fetchCourses();
    if (isAdmin) {
      fetchTeachers();
    }
  }, [isAdmin]);

  const handleAssignTeacher = async (courseId) => {
    if (!selectedTeacher) {
      alert('Please select a teacher');
      return;
    }

    try {
      await api.put(`/courses/${courseId}/assign`, { teacherId: selectedTeacher });
      setAssigningCourse(null);
      setSelectedTeacher('');
      fetchCourses(); // Refresh the courses list
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to assign teacher');
    }
  };

  const handleDelete = async (courseId) => {
    if (!window.confirm('Are you sure you want to delete this course?')) return;
    try {
      await api.delete(`/courses/${courseId}`);
      fetchCourses();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete course');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white text-gray-800 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-800 mx-auto mb-4"></div>
          <p className="text-xl">Loading courses...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white text-gray-800 flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl text-red-600">Error: {error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-between items-center mb-4">
            <div className="flex-1"></div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-700 to-gray-900 bg-clip-text text-transparent">
              Course Management
            </h1>
            <div className="flex-1 flex justify-end">
              {isAdmin && (
                <Link 
                  to="/admin/dashboard/course/new" 
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg shadow-lg transition-colors flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  New Course
                </Link>
              )}
            </div>
          </div>
          <p className="text-lg text-gray-600">
            Manage and assign teachers to courses
          </p>
        </div>

        {/* Course Statistics */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <div className="bg-gray-100 backdrop-blur-sm rounded-xl p-6 border border-gray-200 shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Total Courses</p>
                <p className="text-2xl font-bold text-gray-800">{courses.length}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-gray-100 backdrop-blur-sm rounded-xl p-6 border border-gray-200 shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Assigned Courses</p>
                <p className="text-2xl font-bold text-gray-800">{courses.filter(course => course.teacher).length}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-gray-100 backdrop-blur-sm rounded-xl p-6 border border-gray-200 shadow-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-600 text-sm">Unassigned Courses</p>
                <p className="text-2xl font-bold text-gray-800">{courses.filter(course => !course.teacher).length}</p>
              </div>
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.502 0L4.312 15.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            <div className="relative flex-1 max-w-2xl">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search courses by title, code, description, or teacher..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white shadow-sm"
              />
            </div>
            
            <div className="flex gap-3 items-center">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white shadow-sm"
              >
                <option value="all">All Courses</option>
                <option value="assigned">Assigned Only</option>
                <option value="unassigned">Unassigned Only</option>
              </select>
            </div>
          </div>
          
          {/* Results Counter */}
          {(searchQuery || filterStatus !== 'all') && (
            <div className="mt-3 text-sm text-gray-600">
              Showing {courses.filter(course => {
                const query = searchQuery.toLowerCase();
                const matchesSearch = (
                  course.title.toLowerCase().includes(query) ||
                  (course.code && course.code.toLowerCase().includes(query)) ||
                  (course.description && course.description.toLowerCase().includes(query)) ||
                  (course.teacher && course.teacher.name.toLowerCase().includes(query))
                );
                const matchesFilter = filterStatus === 'all' || 
                  (filterStatus === 'assigned' && course.teacher) ||
                  (filterStatus === 'unassigned' && !course.teacher);
                return matchesSearch && matchesFilter;
              }).length} of {courses.length} courses
            </div>
          )}
        </div>

        {/* Course Cards */}
        <div className="grid gap-6">
          {courses.filter(course => {
            const query = searchQuery.toLowerCase();
            const matchesSearch = (
              course.title.toLowerCase().includes(query) ||
              (course.code && course.code.toLowerCase().includes(query)) ||
              (course.description && course.description.toLowerCase().includes(query)) ||
              (course.teacher && course.teacher.name.toLowerCase().includes(query))
            );
            const matchesFilter = filterStatus === 'all' || 
              (filterStatus === 'assigned' && course.teacher) ||
              (filterStatus === 'unassigned' && !course.teacher);
            return matchesSearch && matchesFilter;
          }).length === 0 ? (
            <div className="bg-gray-50 backdrop-blur-sm rounded-xl p-12 border border-gray-200 shadow-lg text-center">
              <p className="text-gray-600 text-lg">
                {searchQuery || filterStatus !== 'all' ? 'No courses match your filters' : 'No courses available'}
              </p>
              {(searchQuery || filterStatus !== 'all') && (
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setFilterStatus('all');
                  }}
                  className="mt-4 text-blue-600 hover:text-blue-800 underline"
                >
                  Clear filters
                </button>
              )}
            </div>
          ) : (
            courses.filter(course => {
              const query = searchQuery.toLowerCase();
              const matchesSearch = (
                course.title.toLowerCase().includes(query) ||
                (course.code && course.code.toLowerCase().includes(query)) ||
                (course.description && course.description.toLowerCase().includes(query)) ||
                (course.teacher && course.teacher.name.toLowerCase().includes(query))
              );
              const matchesFilter = filterStatus === 'all' || 
                (filterStatus === 'assigned' && course.teacher) ||
                (filterStatus === 'unassigned' && !course.teacher);
              return matchesSearch && matchesFilter;
            }).map(course => (
            <div key={course.id} className="bg-gray-50 backdrop-blur-sm rounded-xl p-6 border border-gray-200 hover:bg-gray-100 transition-all duration-300 shadow-lg">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                {/* Course Info */}
                <div className="flex-1 min-w-0">
                  <h3 className="text-xl font-semibold mb-2 text-gray-800">{course.title}</h3>
                  <p className="text-gray-600 mb-3 line-clamp-2">{course.description}</p>
                  
                  {/* Teacher Assignment Section */}
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    <span className="text-sm text-gray-700">Teacher:</span>
                    {assigningCourse === course.id ? (
                      <div className="flex flex-wrap items-center gap-2">
                        <select
                          value={selectedTeacher}
                          onChange={(e) => setSelectedTeacher(e.target.value)}
                          className="bg-white border border-gray-300 rounded-lg px-3 py-1 text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-400"
                        >
                          <option value="" className="text-gray-800">Select Teacher</option>
                          {teachers.map(teacher => (
                            <option key={teacher.id} value={teacher.id} className="text-gray-800">
                              {teacher.name}
                            </option>
                          ))}
                        </select>
                        <button
                          onClick={() => handleAssignTeacher(course.id)}
                          className="bg-green-500 hover:bg-green-600 text-white px-3 py-1 rounded-lg text-sm transition-colors"
                        >
                          Assign
                        </button>
                        <button
                          onClick={() => setAssigningCourse(null)}
                          className="bg-gray-500 hover:bg-gray-600 text-white px-3 py-1 rounded-lg text-sm transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`px-3 py-1 rounded-full text-sm ${
                          course.teacher 
                            ? 'bg-green-100 text-green-700 border border-green-200' 
                            : 'bg-orange-100 text-orange-700 border border-orange-200'
                        }`}>
                          {course.teacher?.name || 'Unassigned'}
                        </span>
                        {isAdmin && (
                          <button
                            onClick={() => setAssigningCourse(course.id)}
                            className="text-blue-600 hover:text-blue-800 text-sm underline transition-colors"
                          >
                            Change Teacher
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-wrap gap-2">
                  <Link
                    to={`${isAdmin ? '/admin' : '/teachers'}/dashboard/courses/${course.id}/lessons`}
                    className="bg-purple-100 hover:bg-purple-200 text-purple-700 hover:text-purple-800 px-4 py-2 rounded-lg border border-purple-200 transition-all duration-200 flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                    </svg>
                    Manage Lessons
                  </Link>
                  {isAdmin && (
                    <>
                      <Link
                        to={`/admin/dashboard/course/${course.id}/edit`}
                        className="bg-blue-100 hover:bg-blue-200 text-blue-700 hover:text-blue-800 px-4 py-2 rounded-lg border border-blue-200 transition-all duration-200 flex items-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        Edit
                      </Link>
                      <button
                        onClick={() => handleDelete(course.id)}
                        className="bg-red-100 hover:bg-red-200 text-red-700 hover:text-red-800 px-4 py-2 rounded-lg border border-red-200 transition-all duration-200 flex items-center gap-2"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        Delete
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))
          )}
        </div>

        {courses.length === 0 && (
          <div className="text-center py-12">
            <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-12 h-12 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold mb-2 text-gray-800">No Courses Found</h3>
            <p className="text-gray-600">No courses have been created yet.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CourseManagementPage;
